import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import ButtonListContext from "@widget/buttons/ButtonListContext";
export const ConfigSectionBarContext = createContext(null);
export { countButtons } from "@widget/buttons/countButtons";
export const ICON_MODE = { icon: true };
import { countButtons } from "@widget/buttons/countButtons";
/**
 * For ConfigurationView: the bar's state and the value its section content reads.
 * `ownCount` is the buttons the view renders itself; with more than two buttons in all they go
 * in the ⋯ sheet, as list rows (`sheetMode`).
 */
export const useConfigSectionBar = (ownCount, sheetMode) => {
    const [navSlot, setNavSlot] = useState(null);
    const [buttonsSlot, setButtonsSlot] = useState(null);
    const [hostSlot, setHostSlot] = useState(null);
    const [navOwners, setNavOwners] = useState([]);
    const [buttonCounts, setButtonCounts] = useState(new Map());
    const setHasNav = useCallback((owner, has) => setNavOwners(prev => has ? (prev.includes(owner) ? prev : [...prev, owner]) : prev.filter(o => o !== owner)), []);
    const setButtonCount = useCallback((owner, count) => setButtonCounts(prev => {
        if ((prev.get(owner) || 0) === count)
            return prev;
        const next = new Map(prev);
        if (count)
            next.set(owner, count);
        else
            next.delete(owner);
        return next;
    }), []);
    let buttonCount = ownCount;
    buttonCounts.forEach(c => { buttonCount += c; });
    const inSheet = buttonCount > 2;
    // In a list's toolbar, one or two buttons show as icons like the toolbar's own
    const listMode = inSheet ? sheetMode : (hostSlot ? ICON_MODE : null);
    const value = useMemo(() => ({ navSlot, setHasNav, buttonsSlot, listMode, setButtonCount, setHostSlot }), [navSlot, setHasNav, buttonsSlot, listMode, setButtonCount]);
    return { value, hasNav: navOwners.length > 0, buttonCount, inSheet, hostSlot, setNavSlot, setButtonsSlot };
};
/**
 * For tabs inside an open section: true when the tab slider goes in the section bar.
 * ponytail: two sibling tab sets in one section would both land in the bar; none do today.
 */
export const useSectionBarNav = (active) => {
    const bar = useContext(ConfigSectionBarContext);
    const owner = useRef({}).current;
    const inBar = active && !!bar;
    useEffect(() => {
        if (!inBar)
            return;
        bar.setHasNav(owner, true);
        return () => bar.setHasNav(owner, false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inBar, bar === null || bar === void 0 ? void 0 : bar.setHasNav]);
    return { inBar, navSlot: inBar ? bar.navSlot : null };
};
/**
 * For a section header inside an open section: its buttons render in the section bar (or the
 * ⋯ sheet). `inBar` is false outside a section bar; render the header as usual then.
 */
export const useSectionBarButtons = (actions) => {
    const bar = useContext(ConfigSectionBarContext);
    const owner = useRef({}).current;
    const count = bar ? countButtons(actions) : 0;
    useEffect(() => {
        if (!bar)
            return;
        bar.setButtonCount(owner, count);
        return () => bar.setButtonCount(owner, 0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bar === null || bar === void 0 ? void 0 : bar.setButtonCount, count]);
    const portal = (bar === null || bar === void 0 ? void 0 : bar.buttonsSlot) && count > 0
        ? createPortal(bar.listMode
            ? _jsx(ButtonListContext.Provider, { value: bar.listMode, children: actions })
            : actions, bar.buttonsSlot)
        : null;
    return { inBar: !!bar, portal };
};
/**
 * For a list's phone toolbar: a ref for the place where the open section's buttons go, so the
 * section has one bottom bar. Undefined outside a section bar.
 * ponytail: one list per section hosts; a second list in the same section would take over the slot.
 */
export const useSectionBarHost = () => { var _a; return (_a = useContext(ConfigSectionBarContext)) === null || _a === void 0 ? void 0 : _a.setHostSlot; };
